import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api, JourneyPageSectionsColumn, JourneyPageSectionRow } from "@/lib/api";
import {
  JOURNEY_WINDOW_OPTIONS,
  JourneyWindowId,
  journeyWindowParams,
} from "@/lib/journeyWindow";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InfoTip } from "@/components/ui/tooltip";
import StatsExplainer from "@/components/StatsExplainer";
import JourneyEntryGroupsSplit from "@/components/JourneyEntryGroupsSplit";
import { cn } from "@/lib/utils";

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Math.round(v * 1000) / 10}%`;
}

export default function JourneySections() {
  const [period, setPeriod] = useState<JourneyWindowId>("7");

  const query = useQuery({
    queryKey: ["site-analytics-journey-sections", period],
    queryFn: () => api.siteAnalyticsJourney(journeyWindowParams(period)),
    retry: false,
  });

  const pages = query.data?.sections_by_page?.pages ?? [];

  return (
    <div className="grid gap-5">
      <StatsExplainer variant="sekcje" />

      <div className="flex flex-wrap items-center gap-2">
        {JOURNEY_WINDOW_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={opt.id === period ? "default" : "outline"}
            onClick={() => setPeriod(opt.id)}
          >
            {opt.label}
          </Button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">
          {query.data?.period?.label_pl ?? "czas PL"}
          {query.data?.total_sessions != null ? ` · ${query.data.total_sessions} wizyt` : ""}
        </span>
      </div>

      {query.data?.ok && (
        <JourneyEntryGroupsSplit channels={query.data.acquisition_channels} />
      )}

      <p className="text-sm text-muted-foreground">
        Każda kolumna to jedna strona. Procent = ile wizyt na tej stronie dojechało do danej sekcji.
        Obok: ile kliknięć CTA z tego etapu (kto poszedł dalej).
        <InfoTip tipId="section_reach" className="ml-1 inline-flex align-middle" />
        <InfoTip tipId="section_cta" className="ml-1 inline-flex align-middle" />
      </p>

      {query.isLoading && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Wczytywanie sekcji…
          </CardContent>
        </Card>
      )}
      {query.isError && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Nie udało się wczytać danych.
          </CardContent>
        </Card>
      )}

      {query.data && pages.length === 0 && (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Brak danych o sekcjach w tym okresie.
          </CardContent>
        </Card>
      )}

      {pages.length > 0 && (
        <div className="overflow-x-auto pb-2">
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${pages.length}, minmax(240px, 1fr))`,
              minWidth: `${Math.max(pages.length, 1) * 240}px`,
            }}
          >
            {pages.map((col, i) => (
              <PageColumn key={col.page_id} column={col} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PageColumn({
  column,
  index,
}: {
  column: JourneyPageSectionsColumn;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className="min-w-0"
    >
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base leading-snug">{column.label}</CardTitle>
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">{column.page_id}</span>
            {" · "}
            {column.sessions} wizyt
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {column.sections.length === 0 ? (
            <p className="text-sm text-muted-foreground">Brak sekcji.</p>
          ) : (
            column.sections.map((s) => <SectionRow key={s.section_id} section={s} />)
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function SectionRow({ section: s }: { section: JourneyPageSectionRow }) {
  const w = Math.max(4, Math.round((s.view_rate || 0) * 100));
  const clicks = s.cta_clicks ?? 0;
  const topCtas = (s.ctas ?? []).slice(0, 3);

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="truncate font-medium text-foreground/90">
          <span className="mr-1 tabular-nums text-muted-foreground">{s.order + 1}.</span>
          {s.section_id}
        </span>
        <span className="inline-flex shrink-0 items-center gap-1.5 font-mono tabular-nums text-muted-foreground">
          <span title={`Zasięg: ${s.sessions_reached} wizyt`}>
            {pct(s.view_rate)}
          </span>
          <span
            className={cn(
              "min-w-[3.25rem] text-right",
              clicks > 0 ? "text-foreground/80" : "text-muted-foreground/50",
            )}
            title={
              clicks > 0
                ? `${clicks} kliknięć CTA · ${s.cta_sessions ?? 0} wizyt · ${pct(s.cta_rate_of_viewers)} wśród tych, co tu dojechali`
                : "Brak kliknięć CTA z tej sekcji"
            }
          >
            {clicks > 0 ? `${clicks} klik` : "–"}
          </span>
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "h-full rounded-full",
            (s.view_rate || 0) >= 0.5 ? "bg-primary/80" : "bg-primary/45",
          )}
          style={{ width: `${w}%` }}
          title={`${s.sessions_reached} wizyt · wśród kupujących ${pct(s.view_rate_purchased)}`}
        />
      </div>
      {topCtas.length > 0 ? (
        <ul className="space-y-0.5 pl-1">
          {topCtas.map((c) => (
            <li
              key={`${c.intent}-${c.label}`}
              className="truncate text-[10px] leading-tight text-muted-foreground"
              title={`${c.label} (${c.intent}): ${c.clicks}`}
            >
              {c.label} · {c.clicks}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
